/**
 * analyticsReport — RPC contract for the storefront-traffic report callable.
 *
 * Cross-process contract between the admin reports surface's analytics panel
 * and `firebase/src/analytics/report.api.ts`. The panel asks for a
 * range and the callable answers with one row per day, derived from raw buyer
 * timelines plus checkouts and orders. Nothing is precomputed on a schedule,
 * so the knobs below cost nothing to offer — and they are the whole
 * configuration surface there is: a range, the gap that ends a visit, one
 * event, campaign sources, campaign ids, and a closed client filter. A question
 * outside those six is a schema change, never a free-form filter the panel
 * assembles.
 *
 * `.strict()` on every level, both directions: an undeclared key is a
 * rejected call rather than a silently dropped filter that would answer a
 * different question than the one the operator asked.
 *
 * The gate is `tickets.reports.view` + mfa, enforced inside the handler — the
 * same door the buyer documents this reads stand behind, so the callable can
 * never widen what a direct read already refuses.
 *
 * Output fields justified, evidence → signal → commitment:
 *   days           — one row per calendar day in the requested range, in
 *                    ascending date order, each the full derivation for that
 *                    day under the requested gap.
 *   revenueCentsByDay — paid revenue for each of those same days, read live
 *                    from the orders themselves rather than from any cached
 *                    row, since a refund changes what an already-closed day
 *                    earned.
 *   campaignSources — every campaign source the range contains, collected
 *                    before the scope filter runs, so the channel selector
 *                    offers the same roster at every selection.
 *   campaigns      — one row per paid campaign overlapping the range: what it
 *                    cost beside what it sold, which is the whole of cost per
 *                    ticket sold. Read from the campaign documents rather than
 *                    derived here — the spend is Meta's answer and only the
 *                    poller has it.
 *   computedAt     — when THIS answer was derived, ISO-8601, the freshness
 *                    the panel captions itself with.
 *   servedFromCache — whether any day in the answer came from a stored row
 *                    rather than a fresh scan, which is what makes an instant
 *                    answer distinguishable from a recomputed one.
 *   evidence       — how many documents of each kind this call read. The
 *                    provenance panel prints them, so a figure can be checked
 *                    against the database rather than trusted.
 * Excluded:
 *   a per-day computedAt — the stored row keeps its own stamp for the
 *     callable's freshness decision, and the moment a day was derived is not
 *     a question the panel asks; whether it was derived now is, and that is
 *     `ReportedDay.servedFromCache`.
 *   a total row across the range — every figure in it is a sum or a median of
 *     the days already returned, and a second representation is free to
 *     disagree with the days beside it.
 *   a next-page cursor — a range of days is the page.
 */
import { z } from "zod";
import {
	AnalyticsDailySchema,
	CalendarDay,
	ClientBrowserSchema,
	ClientDeviceSchema,
	ClientOrientationSchema,
	ClientOsSchema,
	ClientSemanticsVersionSchema,
	ClientViewportSchema,
	CoverageSchema,
	ReportCoverageSchema,
	VisitGapMinutes,
} from "./buyer.entity.ts";
import {
	CampaignAttributedSchema,
	CampaignObservedSchema,
	CampaignTrafficSchema,
} from "./campaign.entity.ts";
import { Cents } from "./ticket-type.entity.ts";
import { Id } from "./workspace.entity.ts";

export type {
	ClientSemanticsVersion,
	Coverage,
	ReportCoverage,
} from "./buyer.entity.ts";
export { ClientSemanticsVersionSchema, CoverageSchema, ReportCoverageSchema };

/** A count of documents read — never negative, never fractional. */
const Count = z.number().int().min(0);

/** The widest span one call may ask for. A panel preset composes at most a
 *  year, and the callable's own timeout is sized over a month of that span
 *  derived from raw data with nothing cached. */
export const MAX_REPORT_RANGE_DAYS = 366;

/** Whole days from one calendar day to another, both ends counted. */
const spanInDays = (from: string, to: string): number =>
	(Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) /
		86_400_000 +
	1;

// The report accepts only the client dimensions the stored profile already
// carries. Each dimension is nullable so a caller can clear one selection;
// the filter itself defaults to an empty object so existing requests keep
// their unfiltered meaning.
export const ClientFilterSchema = z
	.object({
		os: ClientOsSchema.nullish(),
		browser: ClientBrowserSchema.nullish(),
		device: ClientDeviceSchema.nullish(),
		viewport: ClientViewportSchema.nullish(),
		orientation: ClientOrientationSchema.nullish(),
	})
	.strict()
	.default({});
export type ClientFilter = z.infer<typeof ClientFilterSchema>;
export type ClientFilterPayload = z.input<typeof ClientFilterSchema>;

export const AnalyticsReportInput = z
	.object({
		orgId: Id,
		range: z
			.object({
				from: CalendarDay,
				to: CalendarDay,
			})
			.strict()
			.describe(
				"the days to report on, both ends inclusive, read in the venue's own zone — the later day is never before the earlier one, and the span they enclose is at most a year",
			)
			.superRefine((range, ctx) => {
				const span = spanInDays(range.from, range.to);
				if (span < 1)
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: "the range ends before it starts",
					});
				else if (span > MAX_REPORT_RANGE_DAYS)
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: `a range covers at most ${MAX_REPORT_RANGE_DAYS} days`,
					});
			}),
		visitGapMinutes: VisitGapMinutes.default(30).describe(
			"the minutes of silence that end a visit — thirty is the industry-standard default and the only value whose derivation is ever cached; any other value is derived live for this call alone",
		),
		eventId: z
			.string()
			.min(1)
			.nullish()
			.describe(
				"scopes every figure to one event — the event console asks with its own; absent reports the whole storefront",
			),
		utmSources: z
			.array(z.string().min(1))
			.default([])
			.describe(
				"scopes every figure to buyers whose first touch names one of these campaign sources; an empty set reports every source",
			),
		utmCampaigns: z
			.array(z.string().min(1))
			.default([])
			.describe(
				"scopes every figure to buyers whose first touch names one of these campaigns — the Meta campaign ids the paid creative carries in `utm_campaign`; an empty set reports every campaign. Separate from `utmSources` and never derivable from it: Meta substitutes one of five two-letter platform codes into `utm_source`, so every paid campaign this deployment runs shares a handful of source values and only this knob can isolate one of them",
			),
		client: ClientFilterSchema.describe(
			"scopes every figure to visits whose first valid client profile carries these dimensions; an empty object reports every visit",
		),
	})
	.strict();
export type AnalyticsReportInput = z.infer<typeof AnalyticsReportInput>;
export type AnalyticsReportInputPayload = z.input<typeof AnalyticsReportInput>;

// One day as the derivation produces it — the stored row minus its own
// freshness and evidence stamps, which are internal cache bookkeeping.
// Derived from the stored schema rather than re-declared, so a reshaped row
// reshapes the answer with it. This is what is written to the cache and what
// the pure derivation returns; the wire adds one field to it below.
export const DerivedDaySchema = AnalyticsDailySchema.omit({
	computedAt: true,
	orderEvidenceVersion: true,
}).strict();
export type DerivedDay = z.infer<typeof DerivedDaySchema>;

// One day as the wire carries it. `servedFromCache` reverses this contract's
// own earlier exclusion of per-day cache bookkeeping: the provenance panel is
// exactly the reader for whom that stopped being bookkeeping — an operator
// asking why one day of a range is fresher than its neighbours gets the
// answer per day, where the answer lives, rather than one boolean over
// thirty of them.
export const ReportedDaySchema = DerivedDaySchema.extend({
	clientSemanticsVersion: ClientSemanticsVersionSchema.describe(
		"the visit-entry client-profile semantics used to derive this day; one identifies measured semantics and null identifies a row without that evidence",
	),
	coverage: ReportCoverageSchema.describe(
		"whether the client-filtered answer is complete, partial, or unavailable for each metric",
	),
	servedFromCache: z
		.boolean()
		.describe(
			"whether THIS day came from a stored row rather than a fresh scan — a property of this answer, never of the row, which is why it is added at the wire and not written to the cache",
		),
}).strict();
export type ReportedDay = z.infer<typeof ReportedDaySchema>;

// What the answer cost to produce, counted as it was produced. A day served
// from its stored row reads no timeline and no buyers, so a cache-heavy answer
// honestly reports near-zero raw reads and a row count instead — which is the
// whole point of naming them: the panel's own account of where a figure came
// from is only worth printing if it distinguishes a figure derived now from
// one recalled. The four event counts are the four timeline arms, and
// `checkoutStageEvents` is where every funnel band was drawn from.
//
// `checkoutsRead` is where a funnel band used to be drawn from, and it stays
// for one release's deploy window only. Hosting and Functions do not deploy
// together, so a browser holding the previous bundle parses this answer
// against a schema that still requires the field, and a page cached before the
// deploy keeps doing so for as long as it is open. The server emits a constant
// zero, which is the truth — no figure reads a checkout document any more —
// and the field retires in the release AFTER the one that ships
// `checkoutStageEvents`, once no bundle requiring it can still be running.
//
// `checkoutStageEvents` is optional for the SAME window read from the other
// side, and that symmetry is not decoration. Hosting deploying without
// Functions is not a hypothetical: it happened on this field's own release —
// hosting landed, the functions deploy failed, and every admin opening the
// panel met a parse error on a field the still-running server had never heard
// of. A field added on one side of a two-sided deploy is absent in whichever
// direction lands first, so it is optional until both sides carry it, and it
// becomes required in the release after that.
const EvidenceSchema = z
	.object({
		pageViewEvents: Count,
		basketChangeEvents: Count,
		orderActionEvents: Count,
		checkoutStageEvents: Count.nullish(),
		buyerRecords: Count,
		checkoutsRead: Count.nullish(),
		ordersRead: Count,
		cachedRowsRead: Count,
	})
	.strict();
export type ReportEvidence = z.infer<typeof EvidenceSchema>;

/**
 * One paid campaign as the panel reports on it: what it cost, what Meta says
 * it delivered, and what this product itself watched it sell — the evidence
 * COST PER TICKET SOLD is computed from, never that ratio itself.
 *
 * The ratio is deliberately absent. It is `spendMinor / observed.ticketsSold`,
 * a division by a figure that is legitimately zero for most of a campaign's
 * life, and a schema carrying it would have to choose what zero divides to —
 * infinity, null, or a lie. The panel divides where it renders, and where it
 * can also say "nothing yet" in words.
 *
 * Cost per VISIT is absent for a different reason, and it is a stance rather
 * than an omission: this deployment optimises delivery for landing-page views
 * rather than purchases, so Meta is steering toward the click and not the
 * buyer, and cost per visit is the figure that will always flatter the
 * campaign. The honest one is per ticket sold, and it is the one this contract
 * carries the parts for.
 *
 * Fields justified:
 *   campaignId    — this deployment's own campaign document id; the panel
 *                   drills to the console surface with it
 *   eventId       — which event it promotes, so a workspace-wide report can
 *                   group without a second read
 *   metaCampaignId— the value the creative substitutes into `utm_campaign`,
 *                   and therefore the key `AnalyticsReportInput.utmCampaigns`
 *                   is filtered on — the roster and the knob speak one id
 *   launchedAt    — when the money was committed and this run began
 *                   delivering. Every id above is opaque and stays off the
 *                   surface, so this is the only thing on the row a person can
 *                   call a run by, and this is the surface that needs one: the
 *                   console shows ONE campaign per event and names it by the
 *                   event, which is the identical word on every row here
 *   stoppedAt     — when the spend stopped, so a finished run reads as the
 *                   span it occupied and a live one as an open one. Stamped by
 *                   the operator's own stop and by the run reaching its
 *                   scheduled end alike, and cleared again by a resume, so its
 *                   absence means the spend is open now
 *
 *     Both cross as ISO-8601 strings — the substitution `readAt` makes below,
 *     for the reason stated there. Both nullish, because the entity's refine
 *     states the confirmation's member (`launchedBy`) and not its instant, and
 *     a stored campaign carrying live spend and no stamp is a row this roster
 *     must still report rather than drop for want of a name.
 *   spendMinor    — what it has cost, in the AD ACCOUNT's minor unit
 *   currency      — which unit that is; an ad account is free to bill in one
 *                   this product's own euro-cent figures are not in, so the
 *                   two never share a unit by assumption
 *   attributed    — Meta's own claim about its own delivery
 *   observed      — what this product watched happen
 *
 *     Both are `.omit`ed from the STORED shapes rather than re-declared beside
 *     them, so a field either one grows is a field this row grows — with the
 *     one substitution the wire forces: `readAt` is a Firestore `Timestamp` at
 *     rest and an ISO-8601 string here, because a callable response is JSON
 *     and a Timestamp does not survive it. `computedAt` above already draws
 *     that same line. Each half keeps its OWN stamp: a throttled ad account
 *     leaves Meta's figures stale while this product's stay current, and one
 *     stamp over both would date the fresh half by the stale one.
 *   traffic       — the verdict that separates a campaign nobody responded to
 *                   from one whose responses cannot be seen; without it those
 *                   two render identically and only one is a bug
 *   window        — the attribution window `attributed` holds under; a figure
 *                   is never quoted without the terms it was read under
 * Excluded:
 *   costPerTicketSold, costPerVisit, roas — each a ratio of two fields already
 *     here, free to disagree with them, and each undefined at exactly the
 *     moment a campaign is most looked at
 *   endsAt — the run's SCHEDULED end, derived from the event's own public
 *     ticket-sales end, so every campaign promoting one event carries the
 *     identical instant: on an event-scoped report it is the same value on
 *     every row, the exact collapse the two stamps above are here to undo. The
 *     moment it becomes a fact about a run rather than about the event is the
 *     moment the poller stamps `stoppedAt` from it.
 *   a running/stopped discriminant over those two stamps — `status` is the
 *     authoritative answer to whether a run still delivers and it is the
 *     campaign document's to give, so a union discriminated on `stoppedAt`'s
 *     nullability would be a second answer, free to disagree with the one the
 *     console reads.
 *   proposedAt — when the COMPOSITION parked, before any money existed. A card
 *     leading with it would date a run by an instant it was not running at.
 *   an operator-typed label — the operator states one amount to launch a
 *     campaign and nothing else, so no such string exists on the entity to
 *     forward; the two stamps are what a person calls a run they ran.
 *   the campaign's budget, status, creative — the console reads the campaign
 *     document for all three; this row exists to carry the join, not to become
 *     a second projection of the entity
 */
export const CampaignCostSchema = z
	.object({
		campaignId: z.string().min(1),
		eventId: z.string().min(1),
		metaCampaignId: z.string().min(1),
		launchedAt: z
			.string()
			.datetime()
			.nullish()
			.describe(
				"when the money was committed and this run began delivering, ISO-8601 — absent on a campaign whose confirmation left no stamp",
			),
		stoppedAt: z
			.string()
			.datetime()
			.nullish()
			.describe(
				"when this run's spend stopped, ISO-8601, whether an operator pressed stop or the run reached its own scheduled end — absent while it is still spending",
			),
		spendMinor: z.number().int().min(0),
		currency: z.string().length(3).nullish(),
		attributed: CampaignAttributedSchema.omit({ readAt: true })
			.extend({ readAt: z.string().datetime() })
			.nullish(),
		observed: CampaignObservedSchema.omit({ readAt: true })
			.extend({ readAt: z.string().datetime() })
			.nullish(),
		traffic: CampaignTrafficSchema.describe(
			"whether the UTM join is finding this campaign's arrivals at all — `untraceable` means every observed figure beside it is meaningless rather than zero",
		),
		window: z.string().nullish(),
	})
	.strict();
export type CampaignCost = z.infer<typeof CampaignCostSchema>;

export const AnalyticsReportOutput = z
	.object({
		days: z
			.array(ReportedDaySchema)
			.describe(
				"one row per day in the requested range, ascending; a day with no activity is a row of zeros, never a gap",
			),
		revenueCentsByDay: z
			.record(CalendarDay, Cents)
			.describe(
				"paid revenue per day in the range, derived live from orders (paidAt day; sum of lines[].amount less refundedAmount; scoped by eventId/utmSources like the rows) — NEVER cached, because refunds mutate orders after the fact; a day with no revenue is 0, never absent",
			),
		campaignSources: z
			.array(z.string())
			.describe(
				"every campaign source appearing in the range, collected before the scope filter runs — the channel selector's own roster, so it holds every channel at any selection rather than collapsing to the selected one",
			),
		campaigns: z
			.array(CampaignCostSchema)
			.describe(
				"one row per paid campaign whose own run overlaps the requested range, each carrying what it cost beside what it sold — the roster the campaign selector offers AND the evidence cost per ticket sold is computed from, so a panel never has to ask twice",
			),
		computedAt: z
			.string()
			.datetime()
			.describe(
				"when this answer was derived, ISO-8601 — the freshness the panel captions itself with",
			),
		servedFromCache: z
			.boolean()
			.describe(
				"whether any day in this answer came from a stored row rather than a fresh scan",
			),
		evidence: EvidenceSchema.describe(
			"how many documents of each kind THIS call read, summed over the range — the panel's own account of what a figure was derived from",
		),
	})
	.strict();
export type AnalyticsReportOutput = z.infer<typeof AnalyticsReportOutput>;
